import {
  CategoryInfo,
  FinancialItems,
  financialAssetOptions,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import { getFinancialItemType } from 'src/financial/financial-data/tables/FinancialItemGenericTable';

type OwnerShare = {
  /** The client's percentage of the item, 0-100. */
  percentage: number;
  /** No shares were recorded, so the item was split equally between its owners. */
  isAssumed: boolean;
};

export type FinancialRow = {
  id: string;
  /** Client the item hangs off, so a row can be opened from a view that spans several. */
  clientId?: string;
  memberName: string;
  /** Every owner's name, for the family view, where no single member is "me". */
  ownerNames?: string[];
  /**
   * The viewing client's share of the item, for the individual view. Undefined in the family
   * view, where no single member is "me" and the owners are named instead.
   */
  ownerShare?: OwnerShare;
  product: string;
  navProduct: string;
  description?: string;
  provider?: string;
  typeLabel?: string;
  balanceDate?: string;
  /** The client's share of the item, which is its whole value unless `fullBalance` is set. */
  balance: number | null;
  /** The item's whole value, present only on a joint item where `balance` is a share of it. */
  fullBalance?: number;
  isUpdatedAutomatically?: boolean;
  canConnectToFeed?: boolean;
};

type OwnershipContext = { clientId: string };

type FinancialItemWithOwners = {
  clientId: string;
  isJoint?: boolean | null;
  owners?: {
    clientId?: string | null;
    externalName?: string | null;
    ownershipPercentage?: number | null;
  }[];
};

/**
 * Resolves each recorded owner to a display name: a family member through the roster, an
 * external owner through its own name. Owners neither can name are dropped, so a part-external
 * item still reads differently from a solely owned one.
 */
function ownerNames(
  item: FinancialItemWithOwners,
  resolveOwnerName: ((clientId: string) => string | undefined) | undefined,
): string[] | undefined {
  if (!resolveOwnerName) return undefined;
  const names = (item.owners ?? [])
    .map(owner => (owner.clientId ? resolveOwnerName(owner.clientId) : owner.externalName))
    .filter((name): name is string => !!name);
  return names.length > 0 ? names : undefined;
}

/**
 * The viewing client's share of an item. A sole item is wholly theirs — pensions, crypto and
 * collectibles record no `isJoint` at all, so they always land here.
 *
 * On a joint item the recorded percentages are trusted only when every internal owner carries
 * one, and are then read as weights rather than as literal percentages. This is the
 * normalisation the cashflow engine applies to the same field
 * (`FinancialRecordsSeeder._owner_shares_for`), and it is what keeps the members' shares
 * complementary: nothing yet stops a half-filled or over-100 set being saved (ENG-1648), and
 * taking one owner's 70% at face value while the co-owner falls back to an even split would
 * attribute 120% of the item between them.
 *
 * Otherwise the value splits equally and the share is flagged as assumed. The owners backfill
 * recorded only the known holder of a legacy joint item, so that split is over at least two
 * owners even where the list names one.
 *
 * The fallback is deliberately not the engine's: where an item flags itself joint but names
 * fewer than two owners the engine can attribute to, it keeps the whole value and raises a data
 * gap for the adviser to answer. A gap is no use to a table that has to put something in the
 * cell, so the tab assumes the even split and says so.
 */
function ownerShare(
  item: FinancialItemWithOwners,
  ownership: OwnershipContext | undefined,
): OwnerShare | undefined {
  if (!ownership) return undefined;
  if (!item.isJoint) return { percentage: 100, isAssumed: false };

  const internalOwners = (item.owners ?? []).filter(owner => !!owner.clientId);
  const mine = internalOwners.find(owner => owner.clientId === ownership.clientId);
  const weights = internalOwners.map(owner => owner.ownershipPercentage);
  const total = weights.reduce<number>((sum, weight) => sum + (weight ?? 0), 0);

  if (mine?.ownershipPercentage != null && weights.every(weight => weight != null) && total > 0) {
    return { percentage: (mine.ownershipPercentage / total) * 100, isAssumed: false };
  }
  return { percentage: 100 / Math.max(internalOwners.length, 2), isAssumed: true };
}

/**
 * Narrows a value to the client's share, keeping the whole as context for the row. Totals and
 * net worth then add up the shares rather than counting a joint item in full on both members.
 */
function shareOfBalance(
  balance: number | null,
  share: OwnerShare | undefined,
): { balance: number | null; fullBalance?: number } {
  if (!share || share.percentage === 100 || balance == null) return { balance };
  return { balance: (balance * share.percentage) / 100, fullBalance: balance };
}

export type FinancialSubSection = {
  key: string;
  title: string;
  rows: FinancialRow[];
  total: number;
  /** The same total before shares were applied, present only where the two differ. */
  fullTotal?: number;
};

type FinancialCategoryKey = 'spendingAccounts' | 'assets' | 'liabilities' | 'protections';

export type FinancialCategory = {
  key: FinancialCategoryKey;
  title: string;
  rows: FinancialRow[];
  total: number;
  /** The same total before shares were applied, present only where the two differ. */
  fullTotal?: number;
  totalLabel?: string;
  subSections?: FinancialSubSection[];
};

type GroupedRows = {
  spendingAccounts: FinancialRow[];
  assets: FinancialRow[];
  liabilities: FinancialRow[];
  protections: FinancialRow[];
};

export const emptyGroupedRows = (): GroupedRows => ({
  spendingAccounts: [],
  assets: [],
  liabilities: [],
  protections: [],
});

const sumBalances = (rows: FinancialRow[]) =>
  rows.reduce((acc, row) => acc + (row.balance ?? 0), 0);

/**
 * The shares a section adds up to, and what the same rows come to at their full values. A
 * section holding a joint item does not total the figures the adviser entered, so the full
 * total is carried alongside to say why; it is left off where the two agree.
 */
function totalsFor(rows: FinancialRow[]): { total: number; fullTotal?: number } {
  const total = sumBalances(rows);
  const fullTotal = rows.reduce((acc, row) => acc + (row.fullBalance ?? row.balance ?? 0), 0);
  return fullTotal === total ? { total } : { total, fullTotal };
}

export function extractRows(
  items: FinancialItems,
  memberName: string,
  ownership?: OwnershipContext,
  resolveOwnerName?: (clientId: string) => string | undefined,
): GroupedRows {
  const { metadata } = items;

  return {
    spendingAccounts: items.spendingAccounts.map(item => {
      const share = ownerShare(item, ownership);
      return {
        id: item.id,
        clientId: item.clientId,
        memberName,
        ownerNames: ownerNames(item, resolveOwnerName),
        ownerShare: share,
        product: item.product,
        navProduct: item.product,
        description: item.description,
        provider: item.provider,
        typeLabel: getFinancialItemType(item, metadata),
        balanceDate: item.balanceDate,
        ...shareOfBalance(item.currentBalance, share),
      };
    }),
    assets: items.assets.map(item => {
      const share = ownerShare(item, ownership);
      return {
        id: item.id,
        clientId: item.clientId,
        memberName,
        ownerNames: ownerNames(item, resolveOwnerName),
        ownerShare: share,
        product: item.product,
        navProduct: 'pensionProduct' in item ? item.pensionProduct : item.product,
        description: item.description,
        provider: item.provider,
        typeLabel: getFinancialItemType(item, metadata),
        balanceDate: item.valueDate,
        ...shareOfBalance(item.value, share),
        isUpdatedAutomatically: item.isUpdatedAutomatically,
        canConnectToFeed: item.canConnectToFeed,
      };
    }),
    liabilities: items.liabilities.map(item => {
      const share = ownerShare(item, ownership);
      return {
        id: item.id,
        clientId: item.clientId,
        memberName,
        ownerNames: ownerNames(item, resolveOwnerName),
        ownerShare: share,
        product: item.product,
        navProduct: item.product,
        description: item.description,
        provider: item.provider,
        typeLabel: getFinancialItemType(item, metadata),
        balanceDate: item.remainingBalanceDate,
        ...shareOfBalance(item.remainingBalance, share),
      };
    }),
    // A joint policy pays its cover in full to whoever claims, so a share of it would
    // understate what the client is actually protected for. The share is still shown.
    protections: items.protections.map(item => {
      const share = ownerShare(item, ownership);
      return {
        id: item.id,
        clientId: item.clientId,
        memberName,
        ownerNames: ownerNames(item, resolveOwnerName),
        ownerShare: share,
        product: item.product,
        navProduct: item.product,
        description: item.description,
        provider: item.provider,
        typeLabel: getFinancialItemType(item, metadata),
        balanceDate: undefined,
        balance: item.coverAmount ?? null,
      };
    }),
  };
}

export function mergeGroupedRows(groups: GroupedRows[]): GroupedRows {
  return {
    spendingAccounts: groups.flatMap(group => group.spendingAccounts),
    assets: groups.flatMap(group => group.assets),
    liabilities: groups.flatMap(group => group.liabilities),
    protections: groups.flatMap(group => group.protections),
  };
}

function buildSubSections(
  category: 'ASSET' | 'LIABILITY',
  rows: FinancialRow[],
): FinancialSubSection[] {
  const categoryInfo: CategoryInfo = financialAssetOptions[category];
  const knownProducts = new Set<string>(categoryInfo.productsOrder);
  const sections = categoryInfo.productsOrder
    .map(product => {
      const productRows = rows.filter(row => row.product === product);
      return {
        key: product,
        title: categoryInfo.products[product].label,
        rows: productRows,
        ...totalsFor(productRows),
      };
    })
    .filter(section => section.rows.length > 0);

  // Capture any rows whose product isn't in productsOrder so the displayed
  // sub-sections always reconcile with the section total.
  const otherRows = rows.filter(row => !knownProducts.has(row.product));
  if (otherRows.length > 0) {
    sections.push({ key: 'OTHER', title: 'Other', rows: otherRows, ...totalsFor(otherRows) });
  }

  return sections;
}

/**
 * What the client is worth once their liabilities are taken off. Protection cover is left out:
 * it pays on an event rather than being value held today. Every total is already the client's
 * share, so a joint item moves net worth by what they own of it, not by its whole value.
 */
export function netWorthOf(categories: FinancialCategory[]): number {
  const totalFor = (key: FinancialCategoryKey) =>
    categories.find(category => category.key === key)?.total ?? 0;
  return totalFor('spendingAccounts') + totalFor('assets') - totalFor('liabilities');
}

export function buildCategories(grouped: GroupedRows): FinancialCategory[] {
  return [
    {
      key: 'spendingAccounts',
      title: 'Spending accounts',
      rows: grouped.spendingAccounts,
      ...totalsFor(grouped.spendingAccounts),
    },
    {
      key: 'assets',
      title: 'Assets',
      rows: grouped.assets,
      ...totalsFor(grouped.assets),
      subSections: buildSubSections('ASSET', grouped.assets),
    },
    {
      key: 'liabilities',
      title: 'Liabilities',
      rows: grouped.liabilities,
      ...totalsFor(grouped.liabilities),
      subSections: buildSubSections('LIABILITY', grouped.liabilities),
    },
    {
      key: 'protections',
      title: 'Protection policies',
      rows: grouped.protections,
      ...totalsFor(grouped.protections),
      totalLabel: 'Total cover',
    },
  ];
}
